import { gql, useMutation } from '@apollo/client';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import dayjs from 'dayjs';
import { FC, useState } from 'react';

import Button from 'components/Button';
import Columns from 'components/Columns';
import { FormInput, FormPanel } from 'components/FormPanel';

import countries from 'utils/countries-enum.json';

type UserType = {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  gender: string;
  dateOfBirth: string;
  nationality: string;
  role: string;
};

type UpsertUserMutationResponse = {
  upsertUser: UserType;
};

type UpsertUserMutationVariables = {
  departmentId: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  gender: 'MALE' | 'FEMALE' | 'OTHER';
  dateOfBirth: string | dayjs.Dayjs;
  nationality: 'PAPUA_NEW_GUINEA' | 'AUSTRALIA' | 'SINGAPORE';
  role: 'SUPER_ADMIN' | 'ADMIN' | 'EMPLOYEE';
};

const UPSERT_USER_MUTATION = gql`
  mutation UpsertUser(
    $id: ID
    $departmentId: ID!
    $firstName: String!
    $lastName: String!
    $email: String!
    $password: String
    $gender: GenderEnumType!
    $dateOfBirth: String!
    $nationality: NationalityEnumType!
    $role: RoleEnumType!
  ) {
    upsertUser(
      id: $id
      departmentId: $departmentId
      firstName: $firstName
      lastName: $lastName
      email: $email
      password: $password
      gender: $gender
      dateOfBirth: $dateOfBirth
      nationality: $nationality
      role: $role
    ) {
      id
      department {
        id
        name
      }
      firstName
      lastName
      fullName
      email
      gender
      dateOfBirth
      nationality
      role
    }
  }
`;

const CreateUserDialogButton: FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  const [showDialog, toggleDialog] = useState(false);

  const [upsertUser, { loading: upsertLoading, error: upsertError }] = useMutation<
    UpsertUserMutationResponse,
    UpsertUserMutationVariables
  >(UPSERT_USER_MUTATION, {
    refetchQueries: ['AdministrationUsers'],
  });

  return (
    <>
      <Button onClick={() => toggleDialog(true)}>Add Super Admin User</Button>
      <Dialog
        open={showDialog}
        onClose={() => {
          onClose();
          toggleDialog(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Super Admin User</DialogTitle>
        <DialogContent sx={{ minHeight: '50vh' }}>
          <FormPanel
            loading={upsertLoading}
            error={upsertError}
            onSubmit={data => {
              upsertUser({
                variables: {
                  departmentId: 'bc1d6dd9-f1cf-4d91-a7aa-98b8cf6bad4f',
                  firstName: data.firstName,
                  lastName: data.lastName,
                  email: data.email,
                  password: data.password,
                  gender: data.gender,
                  dateOfBirth: dayjs(data.dateOfBirth).toISOString(),
                  nationality: data.nationality,
                  role: data.role,
                },
              }).then(data => {
                onClose();
                toggleDialog(false);
              });
            }}
            submitButtonLabel="Save and continue"
          >
            <FormInput
              fullWidth
              type="string"
              fieldName="email"
              label="Email"
              defaultValue={''}
              validators={{
                required: true,
                isEmail: true,
              }}
            />
            <Columns number={2}>
              <FormInput
                fullWidth
                type="string"
                fieldName="firstName"
                label="First Name"
                defaultValue={''}
                validators={{
                  required: true,
                }}
              />
              <FormInput
                fullWidth
                type="string"
                fieldName="lastName"
                label="Last Name"
                defaultValue={''}
                validators={{
                  required: true,
                }}
              />
            </Columns>
            <Columns number={2}>
              <FormInput
                fullWidth
                type="select"
                fieldName="gender"
                label="Gender"
                defaultValue={''}
                options={[
                  { value: 'MALE', label: 'Male' },
                  { value: 'FEMALE', label: 'Female' },
                  { value: 'OTHER', label: 'Other' },
                ]}
                validators={{
                  required: true,
                }}
              />
              <FormInput
                fullWidth
                type="date"
                fieldName="dateOfBirth"
                label="Date of Birth"
                defaultValue={''}
                validators={{
                  required: true,
                }}
              />
            </Columns>
            <Columns number={2}>
              <FormInput
                fullWidth
                type="select"
                fieldName="nationality"
                label="Nationality"
                defaultValue={'PAPUA_NEW_GUINEA'}
                options={
                  Object.keys(countries).map(key => ({
                    value: key,
                    label: countries[key],
                  })) as { value: string; label: string }[]
                }
                validators={{
                  required: true,
                }}
              />
              <FormInput
                fullWidth
                type="select"
                fieldName="role"
                label="Role"
                helperText="The role here refers to the permission roles of this dashboard not the person's organisation"
                defaultValue={'SUPER_ADMIN'}
                editable={false}
                options={[
                  { value: 'EMPLOYEE', label: 'Employee' },
                  { value: 'ADMIN', label: 'Admin' },
                  { value: 'SUPER_ADMIN', label: 'Super Admin' },
                ]}
                validators={{
                  required: true,
                }}
              />
            </Columns>
            <FormInput
              fullWidth
              type="string"
              fieldName="password"
              label="Password"
              helperText="Leave this field empty if you do not want to change the password"
              defaultValue={''}
              validators={{
                required: true,
              }}
            />
          </FormPanel>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateUserDialogButton;
